import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { FormCheckbox } from '../../../../components/hook-form/form-checkbox.component';
import FormInput from '../../hooks-form-querybuilder/form-input.component';
import FormDateField, { DateFormat } from '../../hooks-form-querybuilder/form-date.field.component';
import { FormSelect } from '../../hooks-form-querybuilder/form-select.component';
import { useLoadData } from '../../../../hooks/useLoadData';
import { ApiResponse } from '../../../../models/api-response';
import { queryBuilderApiKeyStore } from '../../../../api/query-builder-api-key-store';
import axios from 'axios';
import { getData } from '../../../../api/api-helper';
import { LoadingIndicator } from '../../../../components/loading-indicator.component';
import { FormAutoCompleteSelectDynamic } from '../../hooks-form-querybuilder/form-autocomplete-select.dynamic.component';
import { Label } from '@mui/icons-material';
import { FormAutoCompleteMultiSelectDynamic } from '../../hooks-form-querybuilder/form-autocomplete-multi-select.dynamic.component';
import { DEPENDENCY_API_PARAM_KEY, DEPENDENCY_ORDERS, FIELDS_TO_STORE_DEPENDENCIES } from './constants';

interface Field {
  apiurl: string;
  apiParams: string;
  controlType: string;
  fieldDisplayName: string;
  fieldName: string;
}

const getAddParamKeys = (
  fieldName: string,
  dependencyIds: {
    [key: string]: string;
  } | null
) => {
  const dependencyList: string[] = [];
  DEPENDENCY_ORDERS[fieldName as keyof typeof DEPENDENCY_ORDERS].forEach((dependencyField) => {
    if (dependencyIds?.[dependencyField]) {
      const param = `${DEPENDENCY_API_PARAM_KEY[dependencyField as keyof typeof DEPENDENCY_API_PARAM_KEY]}=${dependencyIds?.[dependencyField]}`;
      dependencyList.push(param);
    }
  });
  return dependencyList?.length > 0 ? dependencyList.join('&') : '';
};

interface LookupColumnProps {
  field: Field;
  filterKey: string;
  groupKey: string;
  name: string;
  optionsRef: React.MutableRefObject<Record<string, any[]>>;
  handleDependencyIds: (fieldName: string, ids: string) => void;
  dependencyIds: {
    [key: string]: string;
  } | null;
}

const CUSTOM_OPTIONS = [
  { value: 'Active', label: 'Active' },
  { value: 'Closed', label: 'Closed' },
];
const CUSTOM_PPISTATUS_OPTIONS = [
  { value: 'Fail', label: 'Fail' },
  { value: 'Pass', label: 'Pass' },
  { value: 'Partial Pass', label: 'Partial Pass' },
  { value: 'Cancelled', label: 'Canceled' },
  { value: 'Waived', label: 'Waived' },
];
const CUSTOM_YESNO_OPTIONS = [
  { value: 'Yes', label: 'Yes' },
  { value: 'No', label: 'No' },
];
const lookupInput: Record<string, React.ComponentType<any>> = {
  Dropdown: FormSelect,
  TextBox: FormInput,
  Datepicker: FormDateField,
  DateTime: FormDateField,
  Boolean: FormCheckbox,
  FreeText: FormInput,
  MultiDropdown: FormAutoCompleteMultiSelectDynamic,
  default: FormSelect,
};

const LookupColumn = ({
  field,
  filterKey,
  groupKey,
  name,
  optionsRef,
  handleDependencyIds,
  dependencyIds,
}: LookupColumnProps) => {
  const { setValue, unregister, watch } = useFormContext();

  const [isLoading, setIsLoading] = useState(false);

  const updatedUrl = field.apiurl?.replace(/^\/api/, '');

  const urlWithParams = useMemo<string>(() => {
    if (!DEPENDENCY_ORDERS[field.fieldName as keyof typeof DEPENDENCY_ORDERS]) {
      return field.apiParams ? `${updatedUrl}?param=${field.apiParams}` : updatedUrl;
    }

    const addParams = getAddParamKeys(field.fieldName, dependencyIds);
    return field.apiParams ? `${updatedUrl}?param=${field.apiParams}&${addParams}` : `${updatedUrl}?param=${addParams}`;
  }, [dependencyIds, field]);

  const fetchLookupData = async () => {
    setIsLoading(true);

    try {
      const response = await getData<ApiResponse<any>>(urlWithParams, {});
      optionsRef.current[filterKey] = response?.data;
    } catch (err) {
      setIsLoading(false);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      if (field.controlType === 'Dropdown' || field.controlType === 'MultiDropdown') {
        if (field?.apiParams?.trim() !== 'projectstatus' && field?.apiParams?.trim() !== 'unitstatus' && field?.apiParams?.trim() !== 'ppistatus'&& field?.apiParams?.trim() !== 'yesnooption') {
          await fetchLookupData();
        }
      }
    };
    fetchData();
  }, [field, filterKey, setValue, name, urlWithParams]);

  useEffect(() => {
    return () => {
      unregister(`groups.${groupKey}.filters.${filterKey}.fieldValue`);
    };
  }, [groupKey, filterKey, unregister]);

  const handleOnChange = (e: any) => {
    if (field.controlType === 'MultiDropdown') {
      const selectedOptions = Array.isArray(e)
        ? optionsRef.current[filterKey]
            ?.filter((item: any) =>
              e.some((criteria: any) => String(criteria.value) === String(item.label) && criteria.label === item.label)
            )
            ?.map((data: any) => data?.value)
        : [];

      const currentFieldValueIdsWithComma = selectedOptions.join(',');

      if (FIELDS_TO_STORE_DEPENDENCIES.includes(field.fieldName)) {
        handleDependencyIds(field.fieldName, currentFieldValueIdsWithComma);
      }

      setValue(`groups.${groupKey}.filters.${filterKey}.fieldIdValue`, currentFieldValueIdsWithComma);
    }
  };

  const LookupInput: React.ComponentType<any> = lookupInput[field?.controlType || 'default'] || lookupInput.default;

  return (
    <>
      {isLoading && <LoadingIndicator isLoading={isLoading} />}
      <LookupInput
        name={name}
        label={field.fieldDisplayName}
        options={
          field?.apiParams?.trim() === 'projectstatus' || field?.apiParams?.trim() === 'unitstatus'
            ? CUSTOM_OPTIONS
            :field?.apiParams?.trim() === 'ppistatus' 
            ? CUSTOM_PPISTATUS_OPTIONS
            :field?.apiParams?.trim() === 'yesnooption' 
            ? CUSTOM_YESNO_OPTIONS
            : optionsRef.current[filterKey]?.map((data) => {
                return {
                  value: data?.label,
                  label: data?.label,
                };
              }) || []
        }
        labelKey="TextValue"
        valueKey="TextValue"
        optionsValueKey="label"
        optionsLabelKey="label"
        required
        format={DateFormat.SIMPLE_DATE}
        isLoading={isLoading}
        watchKeys={[field.fieldDisplayName?.replace(/\s+/g, '')]}
        onChange={(e: any) => handleOnChange(e)}
      />
    </>
  );
};

export default LookupColumn;
